import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../../../../../components/ui/button';
import { Input } from '../../../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../components/ui/select';
import { Checkbox } from '../../../../../components/ui/checkbox';
import { FileText, Filter, BarChart2, Search, X, Loader2, ChevronLeft, ChevronDown, ChevronUp, BookOpen, FileSignature, BookMarked, Download, CheckCircle2, XCircle, Package } from 'lucide-react';
import { ProjectProductionReportModal } from './ProjectProductionReportModal';
import { ReleaseNotesModal } from './ReleaseNotesModal';
import { RegistryBookModal } from './RegistryBookModal';
import { useProductionData } from '../../../../../redux/ProductionData/hooks/useProductionData';
import { GetAllPipeDetails } from '../../../../../redux/Project/setUpSaga';
import { cn } from '../../../../../lib/utils';
import { useDispatch, useSelector } from 'react-redux';
import { getRegistryBookVersions, getMaterialRegistryReportVersions, getRegistryReportVersions, getInspectionPersonnelDocuments } from '../../../../../redux/Project/materialsData/action';
import { ApproveButton } from '../../../../../components/ui/ApproveButton';
import { PdfViewer } from '../../../../../components/viewers/PdfViewer';
import { getIRNReportVersions, getProductionReportVersions, getProjectReportVersions } from '../../../../../redux/ProductionData/actions';
import { IRNReportsViewModal } from './IRNReportsViewModal';
import { ProductionReportsViewModal } from './ProductionReportsViewModal';
import { MaterialReportsViewModal } from '../materials/MaterialReportsViewModal';
import { EquipmentReportsViewModal } from '../equipment/EquipmentReportsViewModal';
import { GenerateProjectReportModal } from './GenerateProjectReportModal';
import { GenerateConformityReportModal } from './GenerateConformityReportModal';
import { ConformityReportsViewModal } from './ConformityReportsViewModal';
import { ProjectReportsViewModal } from './ProjectReportsViewModal';
import { GenerateProductionAssemblyReportModal } from './GenerateProductionAssemblyReportModal';
import { REPORT_TYPES } from '../../../../../redux/ReportDrafts/types';

interface ProjectReportsProps {
  project: any;
}

export const ProjectReports: React.FC<ProjectReportsProps> = ({ project }) => {
  const dispatch = useDispatch();
  const [showWeldBookView, setShowWeldBookView] = useState<boolean>(true);
  const [showRegistryBookView, setShowRegistryBookView] = useState<boolean>(false);
  const [selectedPipeSize, setSelectedPipeSize] = useState<string>('all');
  const [selectedAssemblies, setSelectedAssemblies] = useState<string[]>([]);
  const [reportGenerationLevel, setReportGenerationLevel] = useState<'pipe-size' | 'assembly'>('pipe-size');
  const [pipeSizes, setPipeSizes] = useState<any[]>([]);
  const [assemblies, setAssemblies] = useState<any[]>([]);
  const [filteredAssemblies, setFilteredAssemblies] = useState<any[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [isProjectReportModalOpen, setIsProjectReportModalOpen] = useState(false);
  const [isReleaseNotesModalOpen, setIsReleaseNotesModalOpen] = useState(false);
  const [isRegistryBookModalOpen, setIsRegistryBookModalOpen] = useState(false);
  const [isProductionReportViewModalOpen, setIsProductionReportViewModalOpen] = useState(false);
  const [isReleaseNotesViewModalOpen, setIsReleaseNotesViewModalOpen] = useState(false);
  const [isMaterialReportsViewModalOpen, setIsMaterialReportsViewModalOpen] = useState(false);
  const [isEquipmentReportsViewModalOpen, setIsEquipmentReportsViewModalOpen] = useState(false);
  const [isConsumableReportsViewModalOpen, setIsConsumableReportsViewModalOpen] = useState(false);
  const [isQCReportsViewModalOpen, setIsQCReportsViewModalOpen] = useState(false);
  const [isMiscellaneousReportsViewModalOpen, setIsMiscellaneousReportsViewModalOpen] = useState(false);
  const [isGenerateProjectReportModalOpen, setIsGenerateProjectReportModalOpen] = useState(false);
  const [isGenerateConformityReportModalOpen, setIsGenerateConformityReportModalOpen] = useState(false);
  const [isConformityReportsViewModalOpen, setIsConformityReportsViewModalOpen] = useState(false);
  const [isAssemblyReportsViewModalOpen, setIsAssemblyReportsViewModalOpen] = useState(false);
  const [isGenerateAssemblyReportModalOpen, setIsGenerateAssemblyReportModalOpen] = useState(false);
  const [isProjectReportsViewModalOpen, setIsProjectReportsViewModalOpen] = useState(false);
  const [assemblySearchQuery, setAssemblySearchQuery] = useState<string>('');
  const [isLoadingPipes, setIsLoadingPipes] = useState<boolean>(false);
  const [isVersionsExpanded, setIsVersionsExpanded] = useState<boolean>(false);
  const [viewerFile, setViewerFile] = useState<{ fileUrl: string; fileName: string } | null>(null);
  const [expandedPipeCards, setExpandedPipeCards] = useState<Set<string>>(new Set());

  // Redux state for registry book versions
  const {
    registryBookVersionsLoading,
    registryBookVersions,
    registryBookVersionsError
  } = useSelector((state: any) => state.materialsData || {});

  // Redux state for material registry report versions
  const {
    materialRegistryReportVersionsLoading,
    materialRegistryReportVersions,
    materialRegistryReportVersionsError
  } = useSelector((state: any) => state.materialsData || {});

  // Redux state for IRN report versions
  const {
    irnReportVersionsLoading,
    irnReportVersions,
    irnReportVersionsError
  } = useSelector((state: any) => state.productionData || {});

  // Redux state for production report versions
  const {
    productionReportVersionsLoading,
    productionReportVersions,
    productionReportVersionsError
  } = useSelector((state: any) => state.productionData || {});

  // Redux state for equipment registry report versions
  const {
    registryReportVersionsLoading,
    registryReportVersions,
    registryReportVersionsError,
    inspectionPersonnelDocumentsLoading,
    inspectionPersonnelDocuments,
    inspectionPersonnelDocumentsError
  } = useSelector((state: any) => state.materialsData || {});



  // Fetch material registry report versions when component loads
  useEffect(() => {
    const projectId = project?.project?.projectId || project?.projectId;
    if (projectId) {
      dispatch(getMaterialRegistryReportVersions(projectId));
      dispatch(getIRNReportVersions(projectId));
      dispatch(getProductionReportVersions(projectId));
      dispatch(getRegistryReportVersions(projectId));
      dispatch(getInspectionPersonnelDocuments(projectId));
    }
  }, [project, dispatch]);


  const refresh = (reportType: string) => {
    const projectId = project?.project?.projectId || project?.projectId;
    if (projectId) {
      if (reportType === 'materialRegistryReportVersions') {
        dispatch(getMaterialRegistryReportVersions(projectId));
      } else if (reportType === 'irnReportVersions') {
        dispatch(getIRNReportVersions(projectId));
      } else if (reportType === 'productionReportVersions') {
        dispatch(getProductionReportVersions(projectId));
      } else if (reportType === 'registryReportVersions') {
        dispatch(getRegistryReportVersions(projectId));
      } else if (reportType === 'inspectionPersonnelDocuments') {
        dispatch(getInspectionPersonnelDocuments(projectId));
      } else if (reportType === 'projectReportVersions') {
        dispatch(getProjectReportVersions(projectId));
      }
    }
  }

  // Process registry book versions - sort by version/date, mark latest
  const processedRegistryBookVersions = useMemo(() => {
    if (!registryBookVersions || !Array.isArray(registryBookVersions) || registryBookVersions.length === 0) {
      return { latest: null, older: [] };
    }

    // Sort by docRef or version descending (latest first)
    const sortedVersions = [...registryBookVersions].sort((a: any, b: any) => {
      const refA = a.docRef || a.details?.versionNumber || 0;
      const refB = b.docRef || b.details?.versionNumber || 0;
      return refB - refA;
    });

    const latest = sortedVersions[0] || null;
    const older = sortedVersions.slice(1);

    return { latest, older };
  }, [registryBookVersions]);

  // Format report type for display (e.g., BASE_PIPE_REPORT -> Base Pipe Report)
  const formatReportType = (reportType: string): string => {
    return reportType
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Process material registry report versions - group by report type with approval status
  const processedMaterialVersions = useMemo(() => {
    if (!materialRegistryReportVersions || typeof materialRegistryReportVersions !== 'object') {
      return {};
    }

    const result: Record<string, { latest: any | null; older: any[]; total: number; isApproved: boolean }> = {};

    Object.keys(materialRegistryReportVersions).forEach((reportType) => {
      const docs = materialRegistryReportVersions[reportType] || [];
      if (docs.length === 0) return;

      // Sort by docRef descending (latest first)
      const sorted = [...docs].sort((a: any, b: any) => {
        const refA = a.docRef || 0;
        const refB = b.docRef || 0;
        return refB - refA;
      });

      const latest = sorted[0] || null;
      const isApproved = latest?.approvalStatus === 'approved' || latest?.approval_status === 'approved';

      result[reportType] = {
        latest,
        older: sorted.slice(1),
        total: sorted.length,
        isApproved
      };
    });

    return result;
  }, [materialRegistryReportVersions]);

  // Get all approved material reports
  const approvedMaterialReports = useMemo(() => {
    return Object.entries(processedMaterialVersions)
      .filter(([_, data]) => data.isApproved && data.latest)
      .map(([reportType, data]) => ({
        reportType,
        ...data
      }));
  }, [processedMaterialVersions]);

  // Check if all materials are approved
  const allMaterialsApproved = useMemo(() => {
    const materialTypes = Object.keys(processedMaterialVersions);
    if (materialTypes.length === 0) return false;
    return materialTypes.every(type => processedMaterialVersions[type].isApproved);
  }, [processedMaterialVersions]);

  // Process IRN report versions - group by pipe with approval status
  const processedIRNVersions = useMemo(() => {
    if (!irnReportVersions || typeof irnReportVersions !== 'object') {
      return {};
    }

    const result: Record<string, { latest: any | null; older: any[]; total: number; pipeName?: string; pipeSize?: string; isApproved: boolean }> = {};

    Object.keys(irnReportVersions).forEach((pipeId) => {
      const docs = irnReportVersions[pipeId] || [];
      if (docs.length === 0) return;

      // Sort by docRef descending (latest first)
      const sorted = [...docs].sort((a: any, b: any) => {
        const refA = a.docRef || 0;
        const refB = b.docRef || 0;
        return refB - refA;
      });

      // Get pipe info from first document
      const firstDoc = sorted[0];
      const latest = sorted[0] || null;
      const isApproved = latest?.approvalStatus === 'approved' || latest?.approval_status === 'approved';

      result[pipeId] = {
        latest,
        older: sorted.slice(1),
        total: sorted.length,
        pipeName: firstDoc?.pipeName || firstDoc?.details?.pipeName || `Pipe ${pipeId}`,
        pipeSize: firstDoc?.pipeSize || firstDoc?.details?.pipeSize || '',
        isApproved
      };
    });

    return result;
  }, [irnReportVersions]);

  // Get all approved IRN reports
  const approvedIRNReports = useMemo(() => {
    return Object.entries(processedIRNVersions)
      .filter(([_, data]) => data.isApproved && data.latest)
      .map(([pipeId, data]) => ({
        pipeId,
        ...data
      }));
  }, [processedIRNVersions]);

  // Check if all IRN pipes are approved
  const allIRNPipesApproved = useMemo(() => {
    const pipeIds = Object.keys(processedIRNVersions);
    if (pipeIds.length === 0) return false;
    return pipeIds.every(pipeId => processedIRNVersions[pipeId].isApproved);
  }, [processedIRNVersions]);

  // Process production report versions - group by pipe with approval status
  const processedProductionVersions = useMemo(() => {
    if (!productionReportVersions || typeof productionReportVersions !== 'object') {
      return {};
    }

    const result: Record<string, { latest: any | null; older: any[]; total: number; pipeName?: string; pipeSize?: string; isApproved: boolean }> = {};

    Object.keys(productionReportVersions).forEach((pipeId) => {
      const docs = productionReportVersions[pipeId] || [];
      if (docs.length === 0) return;

      // Sort by docRef descending (latest first)
      const sorted = [...docs].sort((a: any, b: any) => {
        const refA = a.docRef || 0;
        const refB = b.docRef || 0;
        return refB - refA;
      });

      // Get pipe info from first document
      const firstDoc = sorted[0];
      const latest = sorted[0] || null;
      const isApproved = latest?.approvalStatus === 'approved' || latest?.approval_status === 'approved';

      result[pipeId] = {
        latest,
        older: sorted.slice(1),
        total: sorted.length,
        pipeName: firstDoc?.pipeName || firstDoc?.details?.pipeName || `Pipe ${pipeId}`,
        pipeSize: firstDoc?.pipeSize || firstDoc?.details?.pipeSize || '',
        isApproved
      };
    });

    return result;
  }, [productionReportVersions]);

  // Get all approved production reports
  const approvedProductionReports = useMemo(() => {
    return Object.entries(processedProductionVersions)
      .filter(([_, data]) => data.isApproved && data.latest)
      .map(([pipeId, data]) => ({
        pipeId,
        ...data
      }));
  }, [processedProductionVersions]);

  // Check if all production pipes are approved
  const allProductionPipesApproved = useMemo(() => {
    const pipeIds = Object.keys(processedProductionVersions);
    if (pipeIds.length === 0) return false;
    return pipeIds.every(pipeId => processedProductionVersions[pipeId].isApproved);
  }, [processedProductionVersions]);

  // Format equipment report type for display
  const formatEquipmentReportType = (reportType: string): string => {
    if (reportType === 'EQUIPMENT_WELDING_REPORT') return 'Welding';
    if (reportType === 'EQUIPMENT_NDE_REPORT') return 'NDE';
    if (reportType === 'EQUIPMENT_QC_REPORT') return 'QC';
    if (reportType === 'EQUIPMENT_COATING_REPORT') return 'Coating';
    if (reportType === 'QC_INSPECTION_PERSONNEL_REPORT') return 'QC Inspection Personnel';
    if (reportType === 'WELDER_CERTIFICATION_QUALIFICATION_REPORT') return 'Welder Certification & Qualification';
    if (reportType === REPORT_TYPES.REGISTRY_MISCELLANEOUS_REPORT) return 'Miscellaneous Registry';
    return reportType
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Format consumable report type for display
  const formatConsumableReportType = (reportType: string): string => {
    if (reportType === 'CONSUMABLE_WIRE_REPORT') return 'Wire';
    if (reportType === 'CONSUMABLE_GAS_REPORT') return 'Gas';
    if (reportType === 'CONSUMABLE_NDE_REPORT') return 'NDE';
    if (reportType === 'CONSUMABLE_COATING_REPORT') return 'Coating';
    return reportType
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Process equipment registry report versions - Welding, NDE, and QC
  const processedEquipmentVersions = useMemo(() => {
    const result: Record<string, { latest: any | null; older: any[]; total: number; isApproved: boolean }> = {};

    // Process registry report versions (Welding, NDE, and QC)
    if (registryReportVersions && typeof registryReportVersions === 'object') {
      Object.keys(registryReportVersions).forEach((reportType) => {
        // Process Welding, NDE, and QC reports
        if (reportType !== 'EQUIPMENT_WELDING_REPORT' && reportType !== 'EQUIPMENT_NDE_REPORT' && reportType !== 'EQUIPMENT_QC_REPORT') {
          return;
        }

        const docs = registryReportVersions[reportType] || [];
        if (docs.length === 0) return;

        // Sort by docRef descending (latest first)
        const sorted = [...docs].sort((a: any, b: any) => {
          const refA = a.docRef || 0;
          const refB = b.docRef || 0;
          return refB - refA;
        });

        const latest = sorted[0] || null;
        const isApproved = latest?.approvalStatus === 'approved' || latest?.approval_status === 'approved';

        result[reportType] = {
          latest,
          older: sorted.slice(1),
          total: sorted.length,
          isApproved
        };
      });
    }

    return result;
  }, [registryReportVersions]);

  // Process consumable registry report versions - Wire, Gas, and NDE
  const processedConsumableVersions = useMemo(() => {
    const result: Record<string, { latest: any | null; older: any[]; total: number; isApproved: boolean }> = {};

    // Process registry report versions (Wire, Gas, and NDE)
    if (registryReportVersions && typeof registryReportVersions === 'object') {
      Object.keys(registryReportVersions).forEach((reportType) => {
        // Process Wire, Gas, NDE, and Coating reports
        if (reportType !== 'CONSUMABLE_WIRE_REPORT' && reportType !== 'CONSUMABLE_GAS_REPORT' && reportType !== 'CONSUMABLE_NDE_REPORT' && reportType !== 'CONSUMABLE_COATING_REPORT') {
          return;
        }

        const docs = registryReportVersions[reportType] || [];
        if (docs.length === 0) return;

        // Sort by docRef descending (latest first)
        const sorted = [...docs].sort((a: any, b: any) => {
          const refA = a.docRef || 0;
          const refB = b.docRef || 0;
          return refB - refA;
        });

        const latest = sorted[0] || null;
        const isApproved = latest?.approvalStatus === 'approved' || latest?.approval_status === 'approved';

        result[reportType] = {
          latest,
          older: sorted.slice(1),
          total: sorted.length,
          isApproved
        };
      });
    }

    return result;
  }, [registryReportVersions]);

  // Miscellaneous registry report + certificate of conformity from the main-side report grouping.
  const processedMiscellaneousVersions = useMemo(() => {
    const result: Record<string, { latest: any | null; older: any[]; total: number; isApproved: boolean }> = {};
    const miscKey = REPORT_TYPES.REGISTRY_MISCELLANEOUS_REPORT;
    const conformityKey = REPORT_TYPES.CERTIFICATE_OF_CONFORMITY_REPORT;
    const assemblyKey = REPORT_TYPES.PRODUCTION_ASSEMBLY_REPORT;

    if (registryReportVersions && typeof registryReportVersions === 'object') {
      [miscKey, assemblyKey].forEach((reportType) => {
        const docs = registryReportVersions[reportType] || [];
        if (docs.length > 0) {
          const sorted = [...docs].sort((a: any, b: any) => {
            const refA = a.docRef || 0;
            const refB = b.docRef || 0;
            return refB - refA;
          });
          const latest = sorted[0] || null;
          result[reportType] = {
            latest,
            older: sorted.slice(1),
            total: sorted.length,
            isApproved: latest?.approvalStatus === 'approved' || latest?.approval_status === 'approved',
          };
        }
      });
    }

    if (inspectionPersonnelDocuments && typeof inspectionPersonnelDocuments === 'object') {
      let conformityDocs: any[] = [];
      const preferred = inspectionPersonnelDocuments[conformityKey];
      if (Array.isArray(preferred) && preferred.length) {
        conformityDocs = preferred;
      } else {
        const altKey = Object.keys(inspectionPersonnelDocuments).find(
          (key) =>
            key !== 'QC_INSPECTION_PERSONNEL_REPORT' &&
            key !== 'WELDER_CERTIFICATION_QUALIFICATION_REPORT' &&
            key.toUpperCase().includes('CONFORMITY') &&
            Array.isArray(inspectionPersonnelDocuments[key]) &&
            inspectionPersonnelDocuments[key].length
        );
        if (altKey) conformityDocs = inspectionPersonnelDocuments[altKey] || [];
      }

      if (conformityDocs.length > 0) {
        const sorted = [...conformityDocs].sort((a: any, b: any) => {
          const refA = a.docRef || 0;
          const refB = b.docRef || 0;
          return refB - refA;
        });
        const latest = sorted[0] || null;
        result[conformityKey] = {
          latest,
          older: sorted.slice(1),
          total: sorted.length,
          isApproved: latest?.approvalStatus === 'approved' || latest?.approval_status === 'approved',
        };
      }
    }

    return result;
  }, [registryReportVersions, inspectionPersonnelDocuments]);

  // Process QC Inspection Personnel and Welder Qualification reports separately
  const processedQCVersions = useMemo(() => {
    const result: Record<string, { latest: any | null; older: any[]; total: number; isApproved: boolean }> = {};
    const personnelReportTypes = [
      'QC_INSPECTION_PERSONNEL_REPORT',
      'WELDER_CERTIFICATION_QUALIFICATION_REPORT',
    ] as const;

    if (inspectionPersonnelDocuments && typeof inspectionPersonnelDocuments === 'object') {
      Object.keys(inspectionPersonnelDocuments).forEach((reportType) => {
        if (!personnelReportTypes.includes(reportType as typeof personnelReportTypes[number])) {
          return;
        }

        const docs = inspectionPersonnelDocuments[reportType] || [];
        if (docs.length === 0) return;

        // Sort by docRef descending (latest first)
        const sorted = [...docs].sort((a: any, b: any) => {
          const refA = a.docRef || 0;
          const refB = b.docRef || 0;
          return refB - refA;
        });

        const latest = sorted[0] || null;
        const isApproved = latest?.approvalStatus === 'approved' || latest?.approval_status === 'approved';

        result[reportType] = {
          latest,
          older: sorted.slice(1),
          total: sorted.length,
          isApproved
        };
      });
    }

    return result;
  }, [inspectionPersonnelDocuments]);

  // Get all approved equipment reports (Welding and NDE only)
  const approvedEquipmentReports = useMemo(() => {
    return Object.entries(processedEquipmentVersions)
      .filter(([_, data]) => data.isApproved && data.latest)
      .map(([reportType, data]) => ({
        reportType,
        ...data
      }));
  }, [processedEquipmentVersions]);

  // Check if all equipment reports are approved (Welding, NDE, and QC)
  const allEquipmentReportsApproved = useMemo(() => {
    const requiredTypes = ['EQUIPMENT_WELDING_REPORT', 'EQUIPMENT_NDE_REPORT', 'EQUIPMENT_QC_REPORT'];
    if (requiredTypes.length === 0) return false;

    // Check if all required types exist and are approved
    return requiredTypes.every(type => {
      const version = processedEquipmentVersions[type];
      return version && version.isApproved;
    });
  }, [processedEquipmentVersions]);

  // Get all approved consumable reports
  const approvedConsumableReports = useMemo(() => {
    return Object.entries(processedConsumableVersions)
      .filter(([_, data]) => data.isApproved && data.latest)
      .map(([reportType, data]) => ({
        reportType,
        ...data
      }));
  }, [processedConsumableVersions]);

  // Check if all consumable reports are approved (Wire, Gas, NDE, and Coating)
  const allConsumableReportsApproved = useMemo(() => {
    const requiredTypes = ['CONSUMABLE_WIRE_REPORT', 'CONSUMABLE_GAS_REPORT', 'CONSUMABLE_NDE_REPORT', 'CONSUMABLE_COATING_REPORT'];
    if (requiredTypes.length === 0) return false;

    // Check if all required types exist and are approved
    return requiredTypes.every(type => {
      const version = processedConsumableVersions[type];
      return version && version.isApproved;
    });
  }, [processedConsumableVersions]);

  // Get all approved QC reports
  const approvedQCReports = useMemo(() => {
    return Object.entries(processedQCVersions)
      .filter(([_, data]) => data.isApproved && data.latest)
      .map(([reportType, data]) => ({
        reportType,
        ...data
      }));
  }, [processedQCVersions]);

  // Check if all QC reports are approved
  const allQCReportsApproved = useMemo(() => {
    const qcTypes = Object.keys(processedQCVersions);
    if (qcTypes.length === 0) return false;

    // Check if all QC types exist and are approved
    return qcTypes.every(type => {
      const version = processedQCVersions[type];
      return version && version.isApproved;
    });
  }, [processedQCVersions]);

  const approvedMiscellaneousReports = useMemo(() => {
    return Object.entries(processedMiscellaneousVersions)
      .filter(([_, data]) => data.isApproved && data.latest)
      .map(([reportType, data]) => ({
        reportType,
        ...data,
      }));
  }, [processedMiscellaneousVersions]);

  const allMiscellaneousReportsApproved = useMemo(() => {
    const types = Object.keys(processedMiscellaneousVersions);
    if (types.length === 0) return false;
    return types.every((type) => processedMiscellaneousVersions[type]?.isApproved);
  }, [processedMiscellaneousVersions]);

  // Check loading state for equipment reports
  const isEquipmentLoading = registryReportVersionsLoading || inspectionPersonnelDocumentsLoading;
  const equipmentError = registryReportVersionsError || inspectionPersonnelDocumentsError;

  // Check loading state for consumable reports
  const isConsumableLoading = registryReportVersionsLoading;
  const consumableError = registryReportVersionsError;



  // Format date for display
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };



  // Close PDF viewer
  const closeViewer = () => {
    setViewerFile(null);
  };

  // If PDF viewer is open, render only the viewer
  if (viewerFile) {
    return (
      <PdfViewer
        fileUrl={viewerFile.fileUrl}
        fileName={viewerFile.fileName}
        onClose={closeViewer}
      />
    );
  }


  // Render Weld Book card view (initial simple card)
  if (showWeldBookView) {
    return (
      <div className="p-6">
        {/* Report Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Weld Book Card - Clickable */}
          <div
            onClick={() => setShowWeldBookView(false)}
            className="bg-white rounded-xl border border-[#E2E5EA] hover:border-hero-red hover:shadow-[0_8px_24px_rgba(0,39,63,0.08)] transition-all duration-200 cursor-pointer p-4 group"
            style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
          >
            <div className="flex items-start gap-3">
              <div className="w-[30px] h-[30px] rounded-md bg-[rgba(235,28,55,0.08)] flex items-center justify-center flex-shrink-0">
                <BookOpen className="h-4 w-4 text-hero-red" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-[14px] font-semibold text-blueprint-blue mb-1 group-hover:text-hero-red transition-colors">
                  Weld Book
                </h3>
                <p className="text-[12px] text-heritage-grey mb-3 leading-relaxed">
                  Comprehensive weld book reports with production data, weld information, and inspection records.
                </p>
                <div className="inline-flex items-center gap-1 text-[12px] font-medium text-hero-red">
                  <span>Open Weld Book</span>
                  <ChevronLeft className="h-3.5 w-3.5 rotate-180" />
                </div>
              </div>
            </div>
          </div>

          {/* Registry Book Card - Clickable */}
          {/* <div
            onClick={() => {
              setShowWeldBookView(false);
              setShowRegistryBookView(true);
            }}
            className="bg-white rounded-lg shadow-md border-2 border-[#E2E5EA] hover:border-hero-red hover:shadow-lg transition-all duration-200 cursor-pointer p-6 group"
          >
            <div className="flex items-start gap-4">
              <div className="p-3 bg-[rgba(235,28,55,0.1)] rounded-lg group-hover:bg-[rgba(235,28,55,0.15)] transition-colors">
                <BookMarked className="h-8 w-8 text-hero-red" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-blueprint-blue mb-2 group-hover:text-hero-red transition-colors">
                  Registry Book
                </h3>
                <p className="text-sm text-heritage-grey mb-4">
                  View and compile all registry documents including Material Traceability, IRN, QC Personnel, Equipment, and Consumable reports.
                </p>
                <div className="flex items-center text-hero-red font-medium text-sm group-hover:text-hero-red">
                  <span>Open Registry Book</span>
                  <ChevronLeft className="h-4 w-4 ml-1 rotate-180" />
                </div>
              </div>
            </div>
          </div> */}

          {/* Release Notes Card - Clickable
          <div
            onClick={() => setIsReleaseNotesModalOpen(true)}
            className="bg-white rounded-lg shadow-md border-2 border-[#E2E5EA] hover:border-hero-red hover:shadow-lg transition-all duration-200 cursor-pointer p-6 group"
          >
            <div className="flex items-start gap-4">
              <div className="p-3 bg-[rgba(235,28,55,0.1)] rounded-lg group-hover:bg-[rgba(235,28,55,0.15)] transition-colors">
                <FileSignature className="h-8 w-8 text-hero-red" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-blueprint-blue mb-2 group-hover:text-hero-red transition-colors">
                  Release Notes
                </h3>
                <p className="text-sm text-heritage-grey mb-4">
                  Generate Inspection Release Notes (IRN) with activity acceptance, signatures, and traceability documentation.
                </p>
                <div className="flex items-center text-hero-red font-medium text-sm group-hover:text-hero-red">
                  <span>Create Release Note</span>
                  <ChevronLeft className="h-4 w-4 ml-1 rotate-180" />
                </div>
              </div>
            </div>
          </div> */}
        </div>

        {/* Release Notes Modal */}
        {/* <ReleaseNotesModal
          isOpen={isReleaseNotesModalOpen}
          onClose={() => setIsReleaseNotesModalOpen(false)}
          projectId={project?.project?.projectId || project?.projectId}
          projectName={project?.project?.projectName || project?.projectName}
          jobNumber={project?.project?.projectNumber || project?.projectNumber}
        /> */}

        {/* Registry Book Modal */}
        {/* <RegistryBookModal
          isOpen={isRegistryBookModalOpen}
          onClose={() => setIsRegistryBookModalOpen(false)}
          projectId={project?.project?.projectId || project?.projectId}
          projectName={project?.project?.projectName || project?.projectName}
        /> */}
      </div>
    );
  }

  // Render Full Weld Book Details View (after clicking the card)
  return (
    <>
      <div className="p-6">
        {/* Back Button and Header */}
        <div className="mb-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setShowWeldBookView(true)}
              className="flex items-center justify-center w-8 h-8 rounded-lg border border-[#E2E5EA] bg-white hover:bg-page-bg transition-colors"
              title="Back"
            >
              <ChevronLeft className="h-3.5 w-3.5 text-blueprint-blue" />
            </button>
            <h3 className="text-[18px] font-semibold text-blueprint-blue leading-tight">Weld Book</h3>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button
              onClick={() => setIsAssemblyReportsViewModalOpen(true)}
              size="sm"
              // variant="outline"
              className="bg-hero-red hover:bg-hero-red-60 text-white"
            >
              <FileText className="h-4 w-4 mr-2" />
              Assembly Reports
            </Button>
            <Button
              onClick={() => setIsConformityReportsViewModalOpen(true)}
              size="sm"
             // variant="outline"
              className="bg-hero-red hover:bg-hero-red-60 text-white"
            >
              <FileText className="h-4 w-4 mr-2" />
              Conformity Reports
            </Button>
            {/* <Button
              onClick={() => setIsGenerateConformityReportModalOpen(true)}
              size="sm"
              className="bg-hero-red hover:bg-hero-red-60 text-white"
            >
              <FileText className="h-4 w-4 mr-2" />
              Generate Conformity Report
            </Button> */}
            <Button
              onClick={() => setIsProjectReportsViewModalOpen(true)}
              size="sm"
            //  variant="outline"
              className="bg-hero-red hover:bg-hero-red-60 text-white"
            >
              <FileText className="h-4 w-4 mr-2" />
              Project Reports
            </Button>
            {/* <Button
              onClick={() => setIsGenerateProjectReportModalOpen(true)}
              size="sm"
              className="bg-hero-red hover:bg-hero-red-60 text-white"
            >
              <FileText className="h-4 w-4 mr-2" />
              Generate Project Report
            </Button> */}
          </div>
        </div>

        {/* Production Reports Section */}
        <div className="bg-white rounded-xl border border-[#E2E5EA] overflow-hidden mb-6">
          {/* Group header strip */}
          <div className="px-4 py-2.5 bg-page-bg border-b border-[#E2E5EA] flex items-center gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <FileSignature className="h-3.5 w-3.5 text-heritage-grey flex-shrink-0" />
              <h2 className="text-[11px] font-semibold text-heritage-grey uppercase tracking-wider truncate">
                Production Reports (By Pipe Size)
              </h2>
              {Object.keys(processedProductionVersions).length > 0 && (
                <span className="text-[11px] text-heritage-grey">
                  · {Object.keys(processedProductionVersions).length} pipes · {approvedProductionReports.length}/{Object.keys(processedProductionVersions).length} approved
                </span>
              )}
            </div>
          </div>

          {/* Body */}
          {productionReportVersionsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-4 w-4 animate-spin text-heritage-grey mr-2" />
              <span className="text-[13px] text-heritage-grey">Loading production reports...</span>
            </div>
          ) : productionReportVersionsError ? (
            <div className="m-4 flex items-center gap-2 p-3 bg-[rgba(235,28,55,0.06)] border border-[rgba(235,28,55,0.2)] rounded-lg">
              <X className="h-4 w-4 text-hero-red" />
              <span className="text-[13px] text-hero-red">{productionReportVersionsError}</span>
            </div>
          ) : Object.keys(processedProductionVersions).length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-[13px] text-heritage-grey">No production reports generated yet.</p>
            </div>
          ) : (
            <div>
              {Object.entries(processedProductionVersions).map(([pipeId, { latest, total, pipeName, pipeSize, isApproved }], idx) => {
                if (!latest) return null;

                const displayName = pipeSize ? `${pipeName} (${pipeSize})` : pipeName;
                const hasFileUrl = !!(latest.fileUrl || latest.file_url);

                return (
                  <div
                    key={pipeId}
                    className={cn(
                      "flex items-center gap-3 px-4 py-2.5 transition-colors border-t border-[#EEF0F3] first:border-t-0",
                      isApproved ? "hover:bg-page-bg" : "opacity-60"
                    )}
                  >
                    {/* Status icon */}
                    <div className="flex-shrink-0">
                      {isApproved ? (
                        <CheckCircle2 className="h-4 w-4 text-galvanizing-green" />
                      ) : (
                        <XCircle className="h-4 w-4 text-heritage-grey" />
                      )}
                    </div>

                    {/* Name */}
                    <div className="flex-1 min-w-0">
                      <span className="text-[13px] font-medium text-blueprint-blue truncate">{displayName}</span>
                    </div>

                    {/* Versions (clickable — opens the Reports modal) */}
                    <button
                      type="button"
                      onClick={() => setIsProductionReportViewModalOpen(true)}
                      className="flex-shrink-0 hidden sm:inline-block text-[12px] text-hero-red hover:underline w-24 text-left"
                      title="View all versions"
                    >
                      {total} version{total !== 1 ? 's' : ''}
                    </button>

                    {/* Status chip */}
                    <div className="flex-shrink-0 w-24">
                      <span className={cn(
                        "inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full",
                        isApproved
                          ? "text-galvanizing-green bg-[rgba(31,154,58,0.08)]"
                          : "text-heritage-grey bg-page-bg"
                      )}>
                        {isApproved ? 'Approved' : 'Pending'}
                      </span>
                    </div>

                    {/* Date */}
                    <div className="flex-shrink-0 hidden md:block text-[12px] text-heritage-grey w-28">
                      {latest.uploadDate ? formatDate(latest.uploadDate || latest.upload_date || latest.created_at || '') : '-'}
                    </div>

                    {/* Action */}
                    <div className="flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 px-3 text-[12px] border-[#E2E5EA] text-text-grey hover:bg-page-bg disabled:opacity-40"
                        onClick={() => {
                          if (hasFileUrl) {
                            setViewerFile({
                              fileUrl: latest.fileUrl || latest.file_url,
                              fileName: latest.fileName || latest.file_name || displayName
                            });
                          }
                        }}
                        disabled={!isApproved || !hasFileUrl}
                      >
                        <Download className="h-3.5 w-3.5 mr-1.5" />
                        View
                      </Button>
                    </div>
                  </div>
                );
              })}
              {!allProductionPipesApproved && (
                <div className="px-4 py-2.5 bg-[rgba(179,134,0,0.06)] border-t border-[#EEF0F3] text-[11px] text-[#B38600]">
                  ⚠️ All pipe sizes must be approved before merging reports.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Material Reports Section */}
        <div className="bg-white rounded-xl border border-[#E2E5EA] overflow-hidden mb-6">
          <div className="px-4 py-2.5 bg-page-bg border-b border-[#E2E5EA] flex items-center gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <Package className="h-3.5 w-3.5 text-heritage-grey flex-shrink-0" />
              <h2 className="text-[11px] font-semibold text-heritage-grey uppercase tracking-wider truncate">Material Reports</h2>
              {Object.keys(processedMaterialVersions).length > 0 && (
                <span className="text-[11px] text-heritage-grey">
                  · {Object.keys(processedMaterialVersions).length} materials · {approvedMaterialReports.length}/{Object.keys(processedMaterialVersions).length} approved
                </span>
              )}
            </div>
          </div>

          {materialRegistryReportVersionsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-4 w-4 animate-spin text-heritage-grey mr-2" />
              <span className="text-[13px] text-heritage-grey">Loading material reports...</span>
            </div>
          ) : materialRegistryReportVersionsError ? (
            <div className="m-4 flex items-center gap-2 p-3 bg-[rgba(235,28,55,0.06)] border border-[rgba(235,28,55,0.2)] rounded-lg">
              <X className="h-4 w-4 text-hero-red" />
              <span className="text-[13px] text-hero-red">{materialRegistryReportVersionsError}</span>
            </div>
          ) : Object.keys(processedMaterialVersions).length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-[13px] text-heritage-grey">No material reports generated yet.</p>
            </div>
          ) : (
            <div>
              {Object.entries(processedMaterialVersions).map(([reportType, { latest, total, isApproved }]) => {
                if (!latest) return null;

                const displayName = formatReportType(reportType);
                const hasFileUrl = !!latest.fileUrl;

                return (
                  <div
                    key={reportType}
                    className={cn(
                      "flex items-center gap-3 px-4 py-2.5 transition-colors border-t border-[#EEF0F3] first:border-t-0",
                      isApproved ? "hover:bg-page-bg" : "opacity-60"
                    )}
                  >
                    <div className="flex-shrink-0">
                      {isApproved ? <CheckCircle2 className="h-4 w-4 text-galvanizing-green" /> : <XCircle className="h-4 w-4 text-heritage-grey" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-[13px] font-medium text-blueprint-blue truncate">{displayName}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsMaterialReportsViewModalOpen(true)}
                      className="flex-shrink-0 hidden sm:inline-block text-[12px] text-hero-red hover:underline w-24 text-left"
                      title="View all versions"
                    >
                      {total} version{total !== 1 ? 's' : ''}
                    </button>
                    <div className="flex-shrink-0 w-24">
                      <span className={cn(
                        "inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full",
                        isApproved ? "text-galvanizing-green bg-[rgba(31,154,58,0.08)]" : "text-heritage-grey bg-page-bg"
                      )}>
                        {isApproved ? 'Approved' : 'Pending'}
                      </span>
                    </div>
                    <div className="flex-shrink-0 hidden md:block text-[12px] text-heritage-grey w-28">
                      {latest.uploadDate ? formatDate(latest.uploadDate) : '-'}
                    </div>
                    <div className="flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 px-3 text-[12px] border-[#E2E5EA] text-text-grey hover:bg-page-bg disabled:opacity-40"
                        onClick={() => {
                          if (hasFileUrl) {
                            setViewerFile({ fileUrl: latest.fileUrl, fileName: latest.fileName || displayName });
                          }
                        }}
                        disabled={!isApproved || !hasFileUrl}
                      >
                        <Download className="h-3.5 w-3.5 mr-1.5" />
                        View
                      </Button>
                    </div>
                  </div>
                );
              })}
              {!allMaterialsApproved && (
                <div className="px-4 py-2.5 bg-[rgba(179,134,0,0.06)] border-t border-[#EEF0F3] text-[11px] text-[#B38600]">
                  ⚠️ All materials must be approved before merging reports.
                </div>
              )}
            </div>
          )}
        </div>

        {/* IRN Reports Section */}
        <div className="bg-white rounded-xl border border-[#E2E5EA] overflow-hidden mb-6">
          <div className="px-4 py-2.5 bg-page-bg border-b border-[#E2E5EA] flex items-center gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <FileSignature className="h-3.5 w-3.5 text-heritage-grey flex-shrink-0" />
              <h2 className="text-[11px] font-semibold text-heritage-grey uppercase tracking-wider truncate">IRN Reports (By Pipe Size)</h2>
              {Object.keys(processedIRNVersions).length > 0 && (
                <span className="text-[11px] text-heritage-grey">
                  · {Object.keys(processedIRNVersions).length} pipes · {approvedIRNReports.length}/{Object.keys(processedIRNVersions).length} approved
                </span>
              )}
            </div>
          </div>

          {irnReportVersionsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-4 w-4 animate-spin text-heritage-grey mr-2" />
              <span className="text-[13px] text-heritage-grey">Loading IRN reports...</span>
            </div>
          ) : irnReportVersionsError ? (
            <div className="m-4 flex items-center gap-2 p-3 bg-[rgba(235,28,55,0.06)] border border-[rgba(235,28,55,0.2)] rounded-lg">
              <X className="h-4 w-4 text-hero-red" />
              <span className="text-[13px] text-hero-red">{irnReportVersionsError}</span>
            </div>
          ) : Object.keys(processedIRNVersions).length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-[13px] text-heritage-grey">No IRN reports generated yet.</p>
            </div>
          ) : (
            <div>
              {Object.entries(processedIRNVersions).map(([pipeId, { latest, total, pipeName, pipeSize, isApproved }]) => {
                if (!latest) return null;

                const displayName = pipeSize ? `${pipeName} (${pipeSize})` : pipeName;
                const hasFileUrl = !!latest.fileUrl;

                return (
                  <div
                    key={pipeId}
                    className={cn(
                      "flex items-center gap-3 px-4 py-2.5 transition-colors border-t border-[#EEF0F3] first:border-t-0",
                      isApproved ? "hover:bg-page-bg" : "opacity-60"
                    )}
                  >
                    <div className="flex-shrink-0">
                      {isApproved ? <CheckCircle2 className="h-4 w-4 text-galvanizing-green" /> : <XCircle className="h-4 w-4 text-heritage-grey" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-[13px] font-medium text-blueprint-blue truncate">{displayName}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsReleaseNotesViewModalOpen(true)}
                      className="flex-shrink-0 hidden sm:inline-block text-[12px] text-hero-red hover:underline w-24 text-left"
                      title="View all versions"
                    >
                      {total} version{total !== 1 ? 's' : ''}
                    </button>
                    <div className="flex-shrink-0 w-24">
                      <span className={cn(
                        "inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full",
                        isApproved ? "text-galvanizing-green bg-[rgba(31,154,58,0.08)]" : "text-heritage-grey bg-page-bg"
                      )}>
                        {isApproved ? 'Approved' : 'Pending'}
                      </span>
                    </div>
                    <div className="flex-shrink-0 hidden md:block text-[12px] text-heritage-grey w-28">
                      {latest.uploadDate ? formatDate(latest.uploadDate) : '-'}
                    </div>
                    <div className="flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 px-3 text-[12px] border-[#E2E5EA] text-text-grey hover:bg-page-bg disabled:opacity-40"
                        onClick={() => {
                          if (hasFileUrl) {
                            setViewerFile({ fileUrl: latest.fileUrl, fileName: latest.fileName || displayName });
                          }
                        }}
                        disabled={!isApproved || !hasFileUrl}
                      >
                        <Download className="h-3.5 w-3.5 mr-1.5" />
                        View
                      </Button>
                    </div>
                  </div>
                );
              })}
              {!allIRNPipesApproved && (
                <div className="px-4 py-2.5 bg-[rgba(179,134,0,0.06)] border-t border-[#EEF0F3] text-[11px] text-[#B38600]">
                  ⚠️ All pipe sizes must be approved before merging reports.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Equipment Registry Reports Section */}
        <div className="bg-white rounded-xl border border-[#E2E5EA] overflow-hidden mb-6">
          <div className="px-4 py-2.5 bg-page-bg border-b border-[#E2E5EA] flex items-center gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <BookMarked className="h-3.5 w-3.5 text-heritage-grey flex-shrink-0" />
              <h2 className="text-[11px] font-semibold text-heritage-grey uppercase tracking-wider truncate">Equipment Registry Reports</h2>
              {Object.keys(processedEquipmentVersions).length > 0 && (
                <span className="text-[11px] text-heritage-grey">
                  · {Object.keys(processedEquipmentVersions).length} reports · {approvedEquipmentReports.length}/{Object.keys(processedEquipmentVersions).length} approved
                </span>
              )}
            </div>
          </div>

          {isEquipmentLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-4 w-4 animate-spin text-heritage-grey mr-2" />
              <span className="text-[13px] text-heritage-grey">Loading equipment reports...</span>
            </div>
          ) : equipmentError ? (
            <div className="m-4 flex items-center gap-2 p-3 bg-[rgba(235,28,55,0.06)] border border-[rgba(235,28,55,0.2)] rounded-lg">
              <X className="h-4 w-4 text-hero-red" />
              <span className="text-[13px] text-hero-red">{equipmentError}</span>
            </div>
          ) : Object.keys(processedEquipmentVersions).length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-[13px] text-heritage-grey">No equipment reports generated yet.</p>
            </div>
          ) : (
            <div>
              {([
                { key: 'EQUIPMENT_WELDING_REPORT', label: 'Welding' },
                { key: 'EQUIPMENT_NDE_REPORT', label: 'NDE' },
                { key: 'EQUIPMENT_QC_REPORT', label: 'QC' },
              ] as const).map(({ key, label }) => {
                const v = processedEquipmentVersions[key];
                if (!v?.latest) return null;
                const { latest, total, isApproved } = v;
                const hasFileUrl = !!latest.fileUrl;

                return (
                  <div
                    key={key}
                    className={cn(
                      "flex items-center gap-3 px-4 py-2.5 transition-colors border-t border-[#EEF0F3] first:border-t-0",
                      isApproved ? "hover:bg-page-bg" : "opacity-60"
                    )}
                  >
                    <div className="flex-shrink-0">
                      {isApproved ? <CheckCircle2 className="h-4 w-4 text-galvanizing-green" /> : <XCircle className="h-4 w-4 text-heritage-grey" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-[13px] font-medium text-blueprint-blue truncate">{label}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsEquipmentReportsViewModalOpen(true)}
                      className="flex-shrink-0 hidden sm:inline-block text-[12px] text-hero-red hover:underline w-24 text-left"
                      title="View all versions"
                    >
                      {total} version{total !== 1 ? 's' : ''}
                    </button>
                    <div className="flex-shrink-0 w-24">
                      <span className={cn(
                        "inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full",
                        isApproved ? "text-galvanizing-green bg-[rgba(31,154,58,0.08)]" : "text-heritage-grey bg-page-bg"
                      )}>
                        {isApproved ? 'Approved' : 'Pending'}
                      </span>
                    </div>
                    <div className="flex-shrink-0 hidden md:block text-[12px] text-heritage-grey w-28">
                      {latest.uploadDate ? formatDate(latest.uploadDate) : '-'}
                    </div>
                    <div className="flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 px-3 text-[12px] border-[#E2E5EA] text-text-grey hover:bg-page-bg disabled:opacity-40"
                        onClick={() => {
                          if (hasFileUrl) {
                            setViewerFile({ fileUrl: latest.fileUrl, fileName: latest.fileName || `${label} Report` });
                          }
                        }}
                        disabled={!isApproved || !hasFileUrl}
                      >
                        <Download className="h-3.5 w-3.5 mr-1.5" />
                        View
                      </Button>
                    </div>
                  </div>
                );
              })}
              {!allEquipmentReportsApproved && (
                <div className="px-4 py-2.5 bg-[rgba(179,134,0,0.06)] border-t border-[#EEF0F3] text-[11px] text-[#B38600]">
                  ⚠️ All equipment reports (Welding, NDE, QC) must be approved before merging.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Consumable Reports Section */}
        <div className="bg-white rounded-xl border border-[#E2E5EA] overflow-hidden mb-6">
          <div className="px-4 py-2.5 bg-page-bg border-b border-[#E2E5EA] flex items-center gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <FileSignature className="h-3.5 w-3.5 text-heritage-grey flex-shrink-0" />
              <h2 className="text-[11px] font-semibold text-heritage-grey uppercase tracking-wider truncate">Consumable Registry Reports</h2>
              {Object.keys(processedConsumableVersions).length > 0 && (
                <span className="text-[11px] text-heritage-grey">
                  · {Object.keys(processedConsumableVersions).length} reports · {approvedConsumableReports.length}/{Object.keys(processedConsumableVersions).length} approved
                </span>
              )}
            </div>
          </div>

          {isConsumableLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-4 w-4 animate-spin text-heritage-grey mr-2" />
              <span className="text-[13px] text-heritage-grey">Loading consumable reports...</span>
            </div>
          ) : consumableError ? (
            <div className="m-4 flex items-center gap-2 p-3 bg-[rgba(235,28,55,0.06)] border border-[rgba(235,28,55,0.2)] rounded-lg">
              <X className="h-4 w-4 text-hero-red" />
              <span className="text-[13px] text-hero-red">{consumableError}</span>
            </div>
          ) : Object.keys(processedConsumableVersions).length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-[13px] text-heritage-grey">No consumable reports generated yet.</p>
            </div>
          ) : (
            <div>
              {([
                { key: 'CONSUMABLE_WIRE_REPORT', label: 'Wire' },
                { key: 'CONSUMABLE_GAS_REPORT', label: 'Gas' },
                { key: 'CONSUMABLE_NDE_REPORT', label: 'NDE' },
                { key: 'CONSUMABLE_COATING_REPORT', label: 'Coating' },
              ] as const).map(({ key, label }) => {
                const v = processedConsumableVersions[key];
                if (!v?.latest) return null;
                const { latest, total, isApproved } = v;
                const hasFileUrl = !!latest.fileUrl;

                return (
                  <div
                    key={key}
                    className={cn(
                      "flex items-center gap-3 px-4 py-2.5 transition-colors border-t border-[#EEF0F3] first:border-t-0",
                      isApproved ? "hover:bg-page-bg" : "opacity-60"
                    )}
                  >
                    <div className="flex-shrink-0">
                      {isApproved ? <CheckCircle2 className="h-4 w-4 text-galvanizing-green" /> : <XCircle className="h-4 w-4 text-heritage-grey" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-[13px] font-medium text-blueprint-blue truncate">{label}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsConsumableReportsViewModalOpen(true)}
                      className="flex-shrink-0 hidden sm:inline-block text-[12px] text-hero-red hover:underline w-24 text-left"
                      title="View all versions"
                    >
                      {total} version{total !== 1 ? 's' : ''}
                    </button>
                    <div className="flex-shrink-0 w-24">
                      <span className={cn(
                        "inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full",
                        isApproved ? "text-galvanizing-green bg-[rgba(31,154,58,0.08)]" : "text-heritage-grey bg-page-bg"
                      )}>
                        {isApproved ? 'Approved' : 'Pending'}
                      </span>
                    </div>
                    <div className="flex-shrink-0 hidden md:block text-[12px] text-heritage-grey w-28">
                      {latest.uploadDate ? formatDate(latest.uploadDate) : '-'}
                    </div>
                    <div className="flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 px-3 text-[12px] border-[#E2E5EA] text-text-grey hover:bg-page-bg disabled:opacity-40"
                        onClick={() => {
                          if (hasFileUrl) {
                            setViewerFile({ fileUrl: latest.fileUrl, fileName: latest.fileName || `${label} Report` });
                          }
                        }}
                        disabled={!isApproved || !hasFileUrl}
                      >
                        <Download className="h-3.5 w-3.5 mr-1.5" />
                        View
                      </Button>
                    </div>
                  </div>
                );
              })}
              {!allConsumableReportsApproved && (
                <div className="px-4 py-2.5 bg-[rgba(179,134,0,0.06)] border-t border-[#EEF0F3] text-[11px] text-[#B38600]">
                  ⚠️ All consumable reports must be approved before merging.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Miscellaneous Registry Reports Section */}
        <div className="bg-white rounded-xl border border-[#E2E5EA] overflow-hidden mb-6">
          <div className="px-4 py-2.5 bg-page-bg border-b border-[#E2E5EA] flex items-center gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <FileSignature className="h-3.5 w-3.5 text-heritage-grey flex-shrink-0" />
              <h2 className="text-[11px] font-semibold text-heritage-grey uppercase tracking-wider truncate">Miscellaneous & Assembly Reports</h2>
              {Object.keys(processedMiscellaneousVersions).length > 0 && (
                <span className="text-[11px] text-heritage-grey">
                  · {Object.keys(processedMiscellaneousVersions).length} reports · {approvedMiscellaneousReports.length}/{Object.keys(processedMiscellaneousVersions).length} approved
                </span>
              )}
            </div>
          </div>

          {isEquipmentLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-4 w-4 animate-spin text-heritage-grey mr-2" />
              <span className="text-[13px] text-heritage-grey">Loading miscellaneous registry reports...</span>
            </div>
          ) : equipmentError ? (
            <div className="m-4 flex items-center gap-2 p-3 bg-[rgba(235,28,55,0.06)] border border-[rgba(235,28,55,0.2)] rounded-lg">
              <X className="h-4 w-4 text-hero-red" />
              <span className="text-[13px] text-hero-red">{equipmentError}</span>
            </div>
          ) : Object.keys(processedMiscellaneousVersions).length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-[13px] text-heritage-grey">No miscellaneous registry reports generated yet.</p>
            </div>
          ) : (
            <div>
              {([
                { key: REPORT_TYPES.REGISTRY_MISCELLANEOUS_REPORT, label: 'Miscellaneous Registry' },
                { key: REPORT_TYPES.CERTIFICATE_OF_CONFORMITY_REPORT, label: 'Certificate of Conformity' },
                { key: REPORT_TYPES.PRODUCTION_ASSEMBLY_REPORT, label: 'Production Assembly' },
              ] as const).map(({ key, label }) => {
                const v = processedMiscellaneousVersions[key];
                if (!v?.latest) return null;
                const { latest, total, isApproved } = v;
                const hasFileUrl = !!(latest.fileUrl || latest.file_url);

                return (
                  <div
                    key={key}
                    className={cn(
                      "flex items-center gap-3 px-4 py-2.5 transition-colors border-t border-[#EEF0F3] first:border-t-0",
                      isApproved ? "hover:bg-page-bg" : "opacity-60"
                    )}
                  >
                    <div className="flex-shrink-0">
                      {isApproved ? <CheckCircle2 className="h-4 w-4 text-galvanizing-green" /> : <XCircle className="h-4 w-4 text-heritage-grey" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-[13px] font-medium text-blueprint-blue truncate">{label}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsMiscellaneousReportsViewModalOpen(true)}
                      className="flex-shrink-0 hidden sm:inline-block text-[12px] text-hero-red hover:underline w-24 text-left"
                      title="View all versions"
                    >
                      {total} version{total !== 1 ? 's' : ''}
                    </button>
                    <div className="flex-shrink-0 w-24">
                      <span className={cn(
                        "inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full",
                        isApproved ? "text-galvanizing-green bg-[rgba(31,154,58,0.08)]" : "text-heritage-grey bg-page-bg"
                      )}>
                        {isApproved ? 'Approved' : 'Pending'}
                      </span>
                    </div>
                    <div className="flex-shrink-0 hidden md:block text-[12px] text-heritage-grey w-28">
                      {latest.uploadDate ? formatDate(latest.uploadDate || latest.upload_date || latest.created_at || '') : '-'}
                    </div>
                    <div className="flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 px-3 text-[12px] border-[#E2E5EA] text-text-grey hover:bg-page-bg disabled:opacity-40"
                        onClick={() => {
                          if (hasFileUrl) {
                            setViewerFile({
                              fileUrl: latest.fileUrl || latest.file_url,
                              fileName: latest.fileName || latest.file_name || `${label} Report`
                            });
                          }
                        }}
                        disabled={!isApproved || !hasFileUrl}
                      >
                        <Download className="h-3.5 w-3.5 mr-1.5" />
                        View
                      </Button>
                    </div>
                  </div>
                );
              })}
              {!allMiscellaneousReportsApproved && (
                <div className="px-4 py-2.5 bg-[rgba(179,134,0,0.06)] border-t border-[#EEF0F3] text-[11px] text-[#B38600]">
                  ⚠️ Miscellaneous, certificate of conformity, and production assembly reports must be approved before merging.
                </div>
              )}
            </div>
          )}
        </div>

        {/* QC & Welder Qualification Reports Section */}
        <div className="bg-white rounded-xl border border-[#E2E5EA] overflow-hidden mb-6">
          <div className="px-4 py-2.5 bg-page-bg border-b border-[#E2E5EA] flex items-center gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <FileSignature className="h-3.5 w-3.5 text-heritage-grey flex-shrink-0" />
              <h2 className="text-[11px] font-semibold text-heritage-grey uppercase tracking-wider truncate">QC &amp; Welder Qualification Reports</h2>
              {Object.keys(processedQCVersions).length > 0 && (
                <span className="text-[11px] text-heritage-grey">
                  · {Object.keys(processedQCVersions).length} reports · {approvedQCReports.length}/{Object.keys(processedQCVersions).length} approved
                </span>
              )}
            </div>
          </div>

          {isEquipmentLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-4 w-4 animate-spin text-heritage-grey mr-2" />
              <span className="text-[13px] text-heritage-grey">Loading QC and welder qualification reports...</span>
            </div>
          ) : equipmentError ? (
            <div className="m-4 flex items-center gap-2 p-3 bg-[rgba(235,28,55,0.06)] border border-[rgba(235,28,55,0.2)] rounded-lg">
              <X className="h-4 w-4 text-hero-red" />
              <span className="text-[13px] text-hero-red">{equipmentError}</span>
            </div>
          ) : Object.keys(processedQCVersions).length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-[13px] text-heritage-grey">No QC inspection or welder qualification reports generated yet.</p>
            </div>
          ) : (
            <div>
              {([
                { key: 'QC_INSPECTION_PERSONNEL_REPORT', label: 'QC Inspection Personnel' },
                { key: 'WELDER_CERTIFICATION_QUALIFICATION_REPORT', label: 'Welder Certification & Qualification' },
              ] as const).map(({ key, label }) => {
                const v = processedQCVersions[key];
                if (!v?.latest) return null;
                const { latest, total, isApproved } = v;
                const hasFileUrl = !!latest.fileUrl;

                return (
                  <div
                    key={key}
                    className={cn(
                      "flex items-center gap-3 px-4 py-2.5 transition-colors border-t border-[#EEF0F3] first:border-t-0",
                      isApproved ? "hover:bg-page-bg" : "opacity-60"
                    )}
                  >
                    <div className="flex-shrink-0">
                      {isApproved ? <CheckCircle2 className="h-4 w-4 text-galvanizing-green" /> : <XCircle className="h-4 w-4 text-heritage-grey" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-[13px] font-medium text-blueprint-blue truncate">{label}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsQCReportsViewModalOpen(true)}
                      className="flex-shrink-0 hidden sm:inline-block text-[12px] text-hero-red hover:underline w-24 text-left"
                      title="View all versions"
                    >
                      {total} version{total !== 1 ? 's' : ''}
                    </button>
                    <div className="flex-shrink-0 w-24">
                      <span className={cn(
                        "inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full",
                        isApproved ? "text-galvanizing-green bg-[rgba(31,154,58,0.08)]" : "text-heritage-grey bg-page-bg"
                      )}>
                        {isApproved ? 'Approved' : 'Pending'}
                      </span>
                    </div>
                    <div className="flex-shrink-0 hidden md:block text-[12px] text-heritage-grey w-28">
                      {latest.uploadDate ? formatDate(latest.uploadDate) : '-'}
                    </div>
                    <div className="flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 px-3 text-[12px] border-[#E2E5EA] text-text-grey hover:bg-page-bg disabled:opacity-40"
                        onClick={() => {
                          if (hasFileUrl) {
                            setViewerFile({ fileUrl: latest.fileUrl, fileName: latest.fileName || label });
                          }
                        }}
                        disabled={!isApproved || !hasFileUrl}
                      >
                        <Download className="h-3.5 w-3.5 mr-1.5" />
                        View
                      </Button>
                    </div>
                  </div>
                );
              })}
              {!allQCReportsApproved && (
                <div className="px-4 py-2.5 bg-[rgba(179,134,0,0.06)] border-t border-[#EEF0F3] text-[11px] text-[#B38600]">
                  ⚠️ All QC and welder qualification reports must be approved before merging.
                </div>
              )}
            </div>
          )}
        </div>


      </div>


      {isReleaseNotesViewModalOpen && (
        <IRNReportsViewModal
          isOpen={isReleaseNotesViewModalOpen}
          onClose={() => setIsReleaseNotesViewModalOpen(false)}
          projectId={parseInt(project?.project?.projectId) || 0}
          projectName={project.project.projectName}
          jobNumber={project.project.projectNumber}
          refresh={() => refresh('irnReportVersions')}
        />
      )}

      {/* Production Reports View Modal */}
      {isProductionReportViewModalOpen && (
        <ProductionReportsViewModal
          isOpen={isProductionReportViewModalOpen}
          onClose={() => setIsProductionReportViewModalOpen(false)}
          projectId={parseInt(project?.project?.projectId) || 0}
          projectName={project.project.projectName}
          jobNumber={project.project.projectNumber}
          refresh={() => refresh('productionReportVersions')}
        />
      )}

      {/* Material Reports View Modal */}
      {isMaterialReportsViewModalOpen && (
        <MaterialReportsViewModal
          isOpen={isMaterialReportsViewModalOpen}
          onClose={() => setIsMaterialReportsViewModalOpen(false)}
          projectId={parseInt(project?.project?.projectId) || 0}
          projectName={project.project.projectName}
          refresh={() => refresh('materialRegistryReportVersions')}
        />
      )}

      {/* Equipment Reports View Modal */}
      {isEquipmentReportsViewModalOpen && (
        <EquipmentReportsViewModal
          isOpen={isEquipmentReportsViewModalOpen}
          onClose={() => setIsEquipmentReportsViewModalOpen(false)}
          projectId={parseInt(project?.project?.projectId) || 0}
          projectName={project.project.projectName}
          showOnlyQC={false}
          registryType="Equipment"
          refresh={() => refresh('registryReportVersions')}
        />
      )}
       {/* Consumable Reports View Modal */}
       {isConsumableReportsViewModalOpen && (
        <EquipmentReportsViewModal
          isOpen={isConsumableReportsViewModalOpen}
          onClose={() => setIsConsumableReportsViewModalOpen(false)}
          projectId={parseInt(project?.project?.projectId) || 0}
          projectName={project.project.projectName}
          showOnlyQC={false}
          registryType="Consumable"
          refresh={() => refresh('registryReportVersions')}
        />
      )}

      {/* Miscellaneous + Conformity Reports View Modal */}
      {isMiscellaneousReportsViewModalOpen && (
        <EquipmentReportsViewModal
          isOpen={isMiscellaneousReportsViewModalOpen}
          onClose={() => setIsMiscellaneousReportsViewModalOpen(false)}
          projectId={parseInt(project?.project?.projectId) || 0}
          projectName={project.project.projectName}
          showOnlyQC={false}
          registryType="Miscellaneous & Certificate of Conformity"
          refresh={() => {
            refresh('registryReportVersions');
            refresh('inspectionPersonnelDocuments');
          }}
        />
      )}

      {/* QC Reports View Modal */}
      {isQCReportsViewModalOpen && (
        <EquipmentReportsViewModal
          isOpen={isQCReportsViewModalOpen}
          onClose={() => setIsQCReportsViewModalOpen(false)}
          projectId={parseInt(project?.project?.projectId) || 0}
          projectName={project.project.projectName}
          showOnlyQC={true}
          refresh={() => refresh('inspectionPersonnelDocuments')}
        />
      )}

      {/* Generate Project Report Modal */}
      {isGenerateProjectReportModalOpen && (
        <GenerateProjectReportModal
          isOpen={isGenerateProjectReportModalOpen}
          onClose={() => {
            setIsGenerateProjectReportModalOpen(false);
            setIsProjectReportsViewModalOpen(true);
          }}
          projectId={parseInt(project?.project?.projectId) || 0}
          projectName={project.project.projectName}
          jobNumber={project.project.projectNumber}
          approvedProductionReports={approvedProductionReports}
          approvedMaterialReports={approvedMaterialReports}
          approvedIRNReports={approvedIRNReports}
          approvedEquipmentReports={approvedEquipmentReports}
          approvedQCReports={approvedQCReports}
          approvedConsumableReports={approvedConsumableReports}
          approvedMiscellaneousReports={approvedMiscellaneousReports}
        />
      )}

      {isGenerateConformityReportModalOpen && (
        <GenerateConformityReportModal
          isOpen={isGenerateConformityReportModalOpen}
          onClose={() => {
            setIsGenerateConformityReportModalOpen(false);
            setIsConformityReportsViewModalOpen(true);
          }}
          projectId={parseInt(String(project?.project?.projectId || project?.projectId), 10) || 0}
          projectName={project?.project?.projectName || project?.projectName || ''}
          jobNumber={project?.project?.projectNumber || project?.projectNumber || ''}
        />
      )}

      {isConformityReportsViewModalOpen && (
        <ConformityReportsViewModal
          isOpen={isConformityReportsViewModalOpen}
          onClose={() => setIsConformityReportsViewModalOpen(false)}
          onGenerateClick={() => {
            setIsConformityReportsViewModalOpen(false);
            setIsGenerateConformityReportModalOpen(true);
          }}
          projectId={parseInt(String(project?.project?.projectId || project?.projectId), 10) || 0}
          projectName={project?.project?.projectName || project?.projectName || ''}
          refresh={() => refresh('inspectionPersonnelDocuments')}
        />
      )}

      {isAssemblyReportsViewModalOpen && (
        <EquipmentReportsViewModal
          isOpen={isAssemblyReportsViewModalOpen}
          onClose={() => setIsAssemblyReportsViewModalOpen(false)}
          onGenerateClick={() => {
            setIsAssemblyReportsViewModalOpen(false);
            setIsGenerateAssemblyReportModalOpen(true);
          }}
          projectId={parseInt(String(project?.project?.projectId || project?.projectId), 10) || 0}
          projectName={project?.project?.projectName || project?.projectName || ''}
          registryType="Production Assembly"
          refresh={() => refresh('registryReportVersions')}
        />
      )}

      {isGenerateAssemblyReportModalOpen && (
        <GenerateProductionAssemblyReportModal
          isOpen={isGenerateAssemblyReportModalOpen}
          onClose={() => {
            setIsGenerateAssemblyReportModalOpen(false);
            setIsAssemblyReportsViewModalOpen(true);
          }}
          onGenerated={() => refresh('registryReportVersions')}
          projectId={parseInt(String(project?.project?.projectId || project?.projectId), 10) || 0}
          projectName={project?.project?.projectName || project?.projectName || ''}
          jobNumber={project?.project?.projectNumber || project?.projectNumber || ''}
        />
      )}

      {/* Project Reports View Modal */}
      {isProjectReportsViewModalOpen && (
        <ProjectReportsViewModal
          isOpen={isProjectReportsViewModalOpen}
          onClose={() => setIsProjectReportsViewModalOpen(false)}
          onGenerateClick={() => {
            setIsProjectReportsViewModalOpen(false);
            setIsGenerateProjectReportModalOpen(true);
          }}
          projectId={parseInt(project?.project?.projectId) || 0}
          projectName={project.project.projectName}
          jobNumber={project.project.projectNumber}
          refresh={() => refresh('projectReportVersions')}
        />
      )}
    </>
  );
};

